import { ProductionEventSelector as ProductionEventPicker } from "@/shared/selectors/ProductionEventSelector";
import { ErrorModal } from "@dexteel/mesf-core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { ProductionEventOption } from "../../models/ProductionEventOption";
import { getProductionEventsBySection } from "../../repositories/DelaysReportRepository";

type Props = {
	sectionId: number | null;
	delayAreaAssetId: number | null;
	value: number | null;
	millAssetId: number | null;
	onChange: (productionEvent: ProductionEventOption | null) => void;
};

const useProductionEventsBySection = (
	sectionId: number | null,
	delayAreaAssetId: number | null,
	onError?: (error: Error) => void,
) => {
	return useQuery<ProductionEventOption[], Error>({
		queryKey: ["productionEventsBySection", sectionId, delayAreaAssetId],
		queryFn: ({ signal }) =>
			getProductionEventsBySection(
				{
					sectionId: sectionId as number,
					delayAreaAssetId,
				},
				signal,
			),
		enabled: sectionId !== null,
		onError,
	});
};

export const ProductionEventSelector = ({
	sectionId,
	delayAreaAssetId,
	value,
	millAssetId,
	onChange,
}: Props) => {
	const [error, setError] = useState("");

	const { data: productionEvents, isLoading } = useProductionEventsBySection(
		sectionId,
		delayAreaAssetId,
		(error) => setError(error.message),
	);

	useEffect(() => {
		if (
			value !== null &&
			productionEvents &&
			!productionEvents.some(
				(productionEvent) =>
					productionEvent.EventId === value &&
					productionEvent.MillAssetId === millAssetId,
			)
		) {
			onChange(null);
		}
	}, [millAssetId, onChange, productionEvents, value]);

	if (sectionId === null) {
		return null;
	}

	const selected =
		productionEvents?.some(
			(productionEvent) =>
				productionEvent.EventId === value &&
				productionEvent.MillAssetId === millAssetId,
		) === true
			? value
			: null;

	return (
		<>
			<ProductionEventPicker
				options={productionEvents ?? []}
				value={selected}
				isLoading={isLoading}
				emptyText="There are no events for this section."
				onChange={onChange}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading production events"
			/>
		</>
	);
};
